/**
 * Clear in-memory stores and local caches when switching hospitals / logging out.
 * Prevents showing another hospital's patients, doctors, etc. on the same browser.
 */

import { usePatientsStore } from '@/lib/stores/patients';
import { useMasterPatientsStore } from '@/lib/stores/masterPatients';
import { useConsultationsStore } from '@/lib/stores/consultations';
import { useDoctorsStore } from '@/lib/stores/doctors';
import { useServicesStore } from '@/lib/stores/services';
import { useSessionLogsStore } from '@/lib/stores/sessionLogs';
import { usePrescriptionsStore } from '@/lib/stores/prescriptions';

const LEGACY_CACHE_KEYS = [
  'patients-cache',
  'masterPatientsCache',
  'consultationsCache',
  'services-cache',
  'doctors-cache',
  'prescriptions-storage',
  'hospital-storage',
];

/** Prefix for hospital-scoped caches (e.g. patients-cache:<uuid>) */
export function hospitalCacheKey(base: string, hospitalId: string): string {
  return `${base}:${hospitalId}`;
}

export function clearTenantLocalStorage(): void {
  try {
    LEGACY_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));

    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('patients-cache:') ||
          key.startsWith('masterPatientsCache:') ||
          key.startsWith('consultationsCache:') ||
          key.startsWith('services-cache:') ||
          key.startsWith('doctors-cache:'))
      ) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {
    console.warn('Failed to clear tenant localStorage:', e);
  }
}

export function clearTenantDataStores(): void {
  usePatientsStore.getState().resetStore();
  useMasterPatientsStore.getState().resetStore();
  useConsultationsStore.getState().resetStore();
  useDoctorsStore.getState().resetStore();
  useServicesStore.getState().resetStore();
  useSessionLogsStore.getState().clearLogs();
  usePrescriptionsStore.setState({ prescriptions: [] });
  clearTenantLocalStorage();
}
